import React, { useEffect } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { store } from './store/store';
import { checkAuth } from './store/slices/authSlice';
import { PermissionGuard } from './utils/permissions';
import LoginForm from './components/auth/LoginForm';
import AdminLayout from './components/common/AdminLayout';
import NotificationSystem from './components/common/NotificationSystem';
import LoadingSpinner from './components/common/LoadingSpinner';
import Dashboard from './components/dashboard/Dashboard';
import AdminList from './components/admins/AdminList';
import EventDataTable from './components/Events/EventDataTable';
import FormList from './components/Forms/FormList';
import FormBuilder from './components/Forms/FormBuilder';
import FormRender from './components/Forms/FormRender';
import SubmissionsTable from './components/Forms/SubmissionsTable';
import AttendancePage from './components/Attendance/AttendancePage';
import AttendanceMemberFilterPage from './components/Attendance/AttendanceMemberFilterPage';
import CheckInPage from './components/Attendance/CheckInPage';
import TitheReceipts from './pages/TitheReceipts';
import Members from './pages/Members';
import MemberForm from './components/Members/MemberForm';
import MemberDetail from './components/Members/MemberDetail';
import MinorMembers from './pages/MinorMembers';
import MinorMemberForm from './components/MinorMembers/MinorMemberForm';
import MinorMemberDetail from './components/MinorMembers/MinorMemberDetail';
import PublicMinorMemberRegistration from './pages/PublicMinorMemberRegistration';
import PublicMemberRegistration from './pages/PublicMemberRegistration';
import TemporaryMembersManagement from './pages/TemporaryMembersManagement';
import PublicTitheSubmission from './pages/PublicTitheSubmission';
import ServePositions from './pages/ServePositions';
import { OrganizationStructure } from './components/Organization';
import NetworkDebugPage from './components/NetworkDebugPage';
import DiagnosticPage from './pages/DiagnosticPage';
import RoomBookingPage from './components/RoomBooking/RoomBookingPage';
import PublicRoomBooking from './pages/PublicRoomBooking';
import POSPage from './components/POS/POSPage';
import SystemSettings from './pages/SystemSettings';

// Import react-datepicker CSS
import 'react-datepicker/dist/react-datepicker.css';

// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, loading } = useSelector(state => state.auth);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner text={t('loading.loading')} />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AppContent = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, loading } = useSelector(state => state.auth);
  const location = useLocation();
  const { t } = useTranslation();
  const isPublicForm = location.pathname.startsWith('/f/');

  useEffect(() => {
    // Check if user is already authenticated on app start
    dispatch(checkAuth());
  }, [dispatch]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner text={t('loading.loading')} />
      </div>
    );
  }

  // Show login form if not authenticated and not on public pages
  const path = location.pathname || '';
  const isPublicPage =
    path.startsWith('/f/') ||
    path.includes('/register') ||
    path.includes('/tithe-submission') ||
    path.includes('/book-room') ||
    path.includes('/login');
  if (!isAuthenticated && !isPublicPage) {
    return (
      <>
        <LoginForm />
        <NotificationSystem />
      </>
    );
  }

  // Show admin panel routes if authenticated
  return (
    <>
      <Routes>
        {/* Public routes - no authentication required */}
        <Route path="/f/:slug" element={<FormRender />} />
        <Route path="/register" element={<PublicMemberRegistration />} />
        <Route path="/register/children" element={<PublicMinorMemberRegistration type="children" />} />
        <Route path="/register/teenage" element={<PublicMinorMemberRegistration type="teenage" />} />
        <Route path="/book-room" element={<PublicRoomBooking />} />
          <Route path="/tithe-submission" element={<PublicTitheSubmission />} />
        
        {/* Authentication routes */}
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginForm />
        } />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* Admin protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <AdminLayout>
              <Dashboard />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/forms/:id/submissions" element={
          <ProtectedRoute>
            <AdminLayout>
              <SubmissionsTable />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admins" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminList />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/events" element={
          <ProtectedRoute>
            <AdminLayout>
              <EventDataTable />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/forms" element={
          <ProtectedRoute>
            <AdminLayout>
              <FormList />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/forms/new" element={
          <ProtectedRoute>
            <AdminLayout>
              <FormBuilder />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/forms/:id/edit" element={
          <ProtectedRoute>
            <AdminLayout>
              <FormBuilder />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/forms/preview/:slug" element={
          <ProtectedRoute>
            <AdminLayout>
              <FormRender />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/attendance" element={
          <ProtectedRoute>
            <AdminLayout>
              <AttendancePage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/attendance-member" element={
          <ProtectedRoute>
            <AdminLayout>
              <AttendanceMemberFilterPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/check_in" element={
          <ProtectedRoute>
            <AdminLayout>
              <CheckInPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/tithe-receipts" element={
          <ProtectedRoute>
            <AdminLayout>
              <PermissionGuard 
                module="tithe_receipts" 
                fallback={
                  <div className="p-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="text-red-800 font-medium">{t('access.deniedTitle')}</h3>
                      <p className="text-red-600 mt-1">{t('access.deniedDescription')}</p>
                    </div>
                  </div>
                }
              >
                <TitheReceipts />
              </PermissionGuard>
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/members" element={
          <ProtectedRoute>
            <AdminLayout>
              <Members />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/members/new" element={
          <ProtectedRoute>
            <AdminLayout>
              <MemberForm mode="create" />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/members/temporary" element={
          <ProtectedRoute>
            <AdminLayout>
              <TemporaryMembersManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/members/:id" element={
          <ProtectedRoute>
            <AdminLayout>
              <MemberDetail />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/members/:id/edit" element={
          <ProtectedRoute>
            <AdminLayout>
              <MemberForm mode="edit" />
            </AdminLayout>
          </ProtectedRoute>
        } />
        {/* ── Minor Members (Children / Teenage Ministry) ── */}
        <Route path="/minor-members" element={
          <ProtectedRoute>
            <AdminLayout>
              <MinorMembers />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/minor-members/new" element={
          <ProtectedRoute>
            <AdminLayout>
              <MinorMemberForm mode="create" />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/minor-members/new/children" element={
          <ProtectedRoute>
            <AdminLayout>
              <MinorMemberForm mode="create" fixedType="children" />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/minor-members/new/teenage" element={
          <ProtectedRoute>
            <AdminLayout>
              <MinorMemberForm mode="create" fixedType="teenage" />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/minor-members/:id" element={
          <ProtectedRoute>
            <AdminLayout>
              <MinorMemberDetail />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/minor-members/:id/edit" element={
          <ProtectedRoute>
            <AdminLayout>
              <MinorMemberForm mode="edit" />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/serve-positions" element={
          <ProtectedRoute>
            <AdminLayout>
              <ServePositions />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/organization" element={
          <ProtectedRoute>
            <AdminLayout>
              <OrganizationStructure />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/room-booking" element={
          <ProtectedRoute>
            <AdminLayout>
              <RoomBookingPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/pos" element={
          <ProtectedRoute>
            <AdminLayout>
              <POSPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <AdminLayout>
              <SystemSettings />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/debug" element={<NetworkDebugPage />} />
        <Route path="/diagnostic" element={<DiagnosticPage />} />
        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <NotificationSystem />
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <Router>
        <AppContent />
      </Router>
    </Provider>
  );
}

export default App;
